import type { Definition } from '@exodus/dependency-types';
import type { AddressProvider } from '@passkeys/address-provider';
import type { Application } from '@passkeys/application';
import type { PublicKeyProvider } from '@passkeys/public-key-provider';
import type { TransactionSigner } from '@passkeys/transaction-signer';
import type { Transactions } from '@passkeys/transactions';
import type { Wallet } from '@passkeys/wallet-feature';

import { getAppConfig } from '@/utils/app-config';
import { createBitcoinRequestHandler } from './handlers/bitcoin.js';
import { createEthereumRequestHandler } from './handlers/ethereum.js';
import { createSolanaRequestHandler } from './handlers/solana.js';
import { createVerifyOrigin } from './verify-origin/index.js';
import type { Analytics } from '../analytics/types.js';

const BITCOIN_METHOD_REGEX = /^bitcoin_/;
const ETHEREUM_METHOD_REGEX = /^0x[\dA-Fa-f]+_(eth_|wallet_|personal_sign)/;
const SOLANA_METHOD_REGEX = /^sol_/;

type Dependencies = {
  assetClientInterface: unknown;
  addressProvider: AddressProvider;
  analytics: Analytics;
  application: Application;
  assetsModule: unknown;
  blockchainMetadata: unknown;
  enabledAssetsApi: unknown;
  feesApi: unknown;
  lockedAtom: unknown;
  messageSigner: unknown;
  publicKeyProvider: PublicKeyProvider;
  transactions: Transactions;
  transactionSigner: TransactionSigner;
  wallet: Wallet;
  walletAccounts: unknown;
};

const createWeb3 = (exodus: Dependencies) => {
  const handleBitcoinProviderRequest = createBitcoinRequestHandler(exodus);
  const handleEthereumProviderRequest = createEthereumRequestHandler(exodus);
  const handleSolanaProviderRequest = createSolanaRequestHandler(exodus);

  const handleProviderRequest = async (data) => {
    const request = JSON.parse(data);
    const { method } = request;

    // eslint-disable-next-line sonarjs/prefer-string-starts-ends-with
    if (BITCOIN_METHOD_REGEX.test(method)) {
      return handleBitcoinProviderRequest(request);
    }

    if (ETHEREUM_METHOD_REGEX.test(method)) {
      return handleEthereumProviderRequest(request);
    }

    // eslint-disable-next-line sonarjs/prefer-string-starts-ends-with
    if (SOLANA_METHOD_REGEX.test(method)) {
      return handleSolanaProviderRequest(request);
    }
  };

  const verifyOrigin = createVerifyOrigin({
    domains: getAppConfig()?.domains ?? [],
    embeddedMode: !__STANDALONE_MODE__,
  });

  return {
    handleProviderRequest,
    verifyOrigin,
  };
};

const web3Definition = {
  id: 'web3',
  type: 'module',
  factory: createWeb3,
  dependencies: [
    'assetClientInterface',
    'addressProvider',
    'analytics',
    'application',
    'assetsModule',
    'blockchainMetadata',
    'enabledAssetsApi',
    'feesApi',
    'lockedAtom',
    'messageSigner',
    'publicKeyProvider',
    'transactions',
    'transactionSigner',
    'wallet',
    'walletAccounts',
  ],
} as const satisfies Definition;

export type Web3 = ReturnType<typeof web3Definition.factory>;

export default web3Definition;
